import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarDays, CheckCircle2, Loader2, Plus, RotateCcw, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCanonicalClient } from "@/lib/canonicalClient";
import { CATEGORY_OPTIONS, MAX_UPLOAD_BYTES, fmtSize, suggestCategory } from "./fileUtils";
import { suggestDocumentDate } from "./docDate";

export const ACCEPT = "application/pdf,image/*,.doc,.docx,.txt,.csv";
let seq = 0;

const DATE_HINT = {
  day: "Date from file name", day_us: "Date from file name (read as month-first)", month: "Month from file name — set to the 1st", year: "Year from file name — set to 1 Jan" };

export const toItems = (fileList) => Array.from(fileList || []).map((file) => {
  const guess = suggestDocumentDate(file.name);
  return {
    key: `u${++seq}`, file, category: suggestCategory(file.name), description: "",
    document_date: guess?.date || "", date_hint: guess?.precision || null,
    status: file.size > MAX_UPLOAD_BYTES ? "error" : "queued", progress: 0,
    error: file.size > MAX_UPLOAD_BYTES ? "Larger than 25 MB" : null,
  };
});

const selectCls = "h-8 px-2 border border-zinc-300 rounded-md text-xs bg-white";

function StatusCell({ it, onRetry, onRemove, busy }) {
  if (it.status === "done") return <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-label="Uploaded" />;
  if (it.status === "uploading") return <span className="text-2xs font-mono-num text-zinc-500">{it.progress}%</span>;
  const remove = (
    <button type="button" onClick={onRemove} disabled={busy} title="Remove" data-testid={`bulk-remove-${it.key}`}
            className="h-6 w-6 inline-flex items-center justify-center rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700">
      <X className="h-3.5 w-3.5" />
    </button>
  );
  if (it.status !== "error") return remove;
  return (
    <span className="flex items-center gap-1">
      <AlertCircle className="h-4 w-4 text-red-600" />
      {it.file.size <= MAX_UPLOAD_BYTES && (
        <button type="button" onClick={onRetry} disabled={busy} title="Retry" data-testid={`bulk-retry-${it.key}`}
                className="h-6 w-6 inline-flex items-center justify-center rounded hover:bg-zinc-100">
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      )}
      {remove}
    </span>
  );
}

function BulkRow({ it, idx, onChange, onRetry, onRemove, busy }) {
  const locked = busy || it.status === "done" || it.status === "uploading";
  return (
    <li className="py-2 space-y-1.5" data-testid={`bulk-row-${idx}`}>
      <div className="flex items-center gap-2 text-sm">
        <span className="font-medium truncate flex-1" title={it.file.name}>{it.file.name}</span>
        <span className="text-2xs text-zinc-400 font-mono-num shrink-0">{fmtSize(it.file.size)}</span>
        <StatusCell it={it} onRetry={onRetry} onRemove={onRemove} busy={busy} />
      </div>
      <div className="grid grid-cols-[150px_140px_1fr] gap-2">
        <select value={it.category} disabled={locked} onChange={(e) => onChange({ category: e.target.value })}
                className={selectCls} data-testid={`bulk-category-${idx}`}>
          {CATEGORY_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <Input type="date" value={it.document_date} disabled={locked} className="h-8 text-xs"
               onChange={(e) => onChange({ document_date: e.target.value, date_hint: null })} data-testid={`bulk-date-${idx}`} />
        <Input value={it.description} disabled={locked} placeholder="Description (optional)" className="h-8 text-xs"
               onChange={(e) => onChange({ description: e.target.value })} data-testid={`bulk-description-${idx}`} />
      </div>
      {it.date_hint && it.status !== "done" && (
        <div className="text-2xs text-sky-700 flex items-center gap-1" data-testid={`bulk-date-hint-${idx}`}>
          <CalendarDays className="h-3 w-3" /> {DATE_HINT[it.date_hint]} — adjust if needed
        </div>
      )}
      {it.error && <div className="text-2xs text-red-700" data-testid={`bulk-error-${idx}`}>{it.error}</div>}
    </li>
  );
}

export function BulkUploadDialog({ email, initialFiles, onClose, onUploaded }) {
  const canonical = useCanonicalClient();
  const [items, setItems] = useState(() => toItems(initialFiles));
  const [busy, setBusy] = useState(false);
  const addRef = useRef(null);
  const patch = (key, p) => setItems((xs) => xs.map((x) => (x.key === key ? { ...x, ...p } : x)));

  async function uploadOne(it) {
    patch(it.key, { status: "uploading", progress: 0, error: null });
    const fd = new FormData();
    fd.append("file", it.file);
    fd.append("category", it.category);
    fd.append("description", it.description);
    if (it.document_date) fd.append("document_date", it.document_date);
    if (canonical?.clientId) fd.append("canonical_client_id", canonical.clientId);
    try {
      await api.post(`/health-record/${encodeURIComponent(email)}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => e.total && patch(it.key, { progress: Math.round((e.loaded / e.total) * 100) }),
      });
      patch(it.key, { status: "done", progress: 100 });
      return true;
    } catch (e) {
      patch(it.key, { status: "error", error: e?.response?.data?.detail || e.message || "Upload failed" });
      return false;
    }
  }

  async function run(list) {
    setBusy(true);
    let ok = 0;
    for (const it of list) ok += (await uploadOne(it)) ? 1 : 0;
    setBusy(false);
    if (ok) onUploaded?.();
    const failed = list.length - ok;
    if (!failed) { toast.success(`${ok} file${ok === 1 ? "" : "s"} uploaded`); onClose(); }
    else toast.error(`${failed} file${failed === 1 ? "" : "s"} failed`, { description: "Fix or retry the marked rows." });
  }

  const pending = items.filter((x) => x.status === "queued" || (x.status === "error" && x.file.size <= MAX_UPLOAD_BYTES));
  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="bulk-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload {items.length} file{items.length === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription>
            Category and document date are suggested from each file name — change them if needed. PDFs, images, Word, text — up to 25 MB each.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-zinc-500">Apply category to all:</span>
          <select value="" disabled={busy} className={selectCls} data-testid="bulk-category-all"
                  onChange={(e) => { const v = e.target.value; if (v) setItems((xs) => xs.map((x) => (
                    x.status === "done" ? x : { ...x, category: v }))); }}>
            <option value="">Choose…</option>
            {CATEGORY_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <Button size="sm" variant="ghost" className="ml-auto h-8" disabled={busy}
                  onClick={() => addRef.current?.click()} data-testid="bulk-add-more">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add files
          </Button>
          <input ref={addRef} type="file" multiple accept={ACCEPT} className="hidden" data-testid="bulk-add-input"
                 onChange={(e) => { const add = toItems(e.target.files); e.target.value = ""; setItems((xs) => [...xs, ...add]); }} />
        </div>
        <ul className="divide-y divide-zinc-100 max-h-[55vh] overflow-y-auto pr-1" data-testid="bulk-list">
          {items.map((it, i) => (
            <BulkRow key={it.key} it={it} idx={i} busy={busy} onChange={(p) => patch(it.key, p)}
                     onRetry={() => run([it])} onRemove={() => setItems((xs) => xs.filter((x) => x.key !== it.key))} />
          ))}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="bulk-cancel">Close</Button>
          <Button onClick={() => run(pending)} disabled={busy || !pending.length} data-testid="bulk-upload-all">
            {busy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Uploading…</>
              : `Upload ${pending.length} file${pending.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
